import React, { useState, useRef } from "react";
import { Button, Card, CardBody, CardImg, Input, Spinner } from "reactstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faImage, faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { uploadImage, validateVideoClient, uploadWithPresignedUrl } from '../../../../services/middleware';
import { ImageType } from '../../../../constants/General';
import { Actions } from '../../../../redux/actions';
import { dispatch } from '../../../../utils/store';
import { handleError } from '../../../../utils/toast';
import { Info } from 'lucide-react';
import { toast } from "react-toastify";
import ImageFocusModal from "./ImageFocusModal";
import VideoRequirementsModal from './VideoRequirementsModal';

const ImageUpload = ({ setSelectedFile, selectedFile, selectedVideo, setSelectedVideo, event }) => {

  // Handle Image Upload
  // const handleImageUpload = (event) => {
  //   const files = event.target.files;
  //   if (files.length > 0) {
  //     const newImages = Array.from(files).map((file) => ({
  //       eventId: "",
  //       eventMedia: URL.createObjectURL(file),
  //       mediaType: "image"
  //     }));

  //     setSelectedFile((prevImages) => [...prevImages, ...newImages]);
  //   }
  // };
  const [isUploading, setIsUploading] = useState(false);
  const [maxSizeMB, setMaxSizeMB] = useState(30); // Set max size in MB
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const fileType = file?.type?.split('/')[0];

    if (file) {
      if (file.size > maxSizeMB * 1024 * 1024) {
        return toast.warn(`Image size must be under ${maxSizeMB}MB.`, { position: 'bottom-right', theme: 'colored' });
      }
      setIsUploading(true); // start loader
      try {
        const response = await uploadWithPresignedUrl(file, fileType === 'video' ? ImageType.CAMPAIGN_VIDEO : ImageType.CAMPAIGN_IMAGE);
        console.log("response", response);
        const newImage = {
          eventId: event?.id,
          eventMedia: response,
          mediaType: fileType === 'video' ? 'video' : "image"
        };

        setSelectedFile((prevImages) => [...(prevImages || []), newImage]);
      } catch (err) {
        dispatch(Actions.RestaurantAdmin.SetLoading, false);
        handleError(err);
      } finally {
        setIsUploading(false); // stop loader
      }
    }
  };

  // const handleImageUpload = async (e) => {
  //   const file = e.target.files[0];
  //   const fileType = file?.type?.split('/')[0];
  //   console.log("fileType", fileType);
  //   if (file) {
  //     // Validate only if video
  //     setIsUploading(true); // start loader
  //     try {
  //       const response = await uploadImage(file, fileType === 'video' ? ImageType.CAMPAIGN_VIDEO : ImageType.CAMPAIGN_IMAGE);

  //       const newImage = {
  //         eventId: event?.id,
  //         eventMedia: response.fileLink,
  //         mediaType: fileType === 'video' ? 'video' : "image"
  //       };

  //       setSelectedFile((prevImages) => [...(prevImages || []), newImage]);
  //     } catch (err) {
  //       dispatch(Actions.RestaurantAdmin.SetLoading, false);
  //       handleError(err);
  //     } finally {
  //       setIsUploading(false); // stop loader
  //     }
  //   }
  // };


  const [isVideoUploading, setIsVideoUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);

  // Handle Video Upload
  const handleVideoUpload = async (event) => {
    const file = event.target.files[0];
    const fileType = file?.type?.split('/')[0];
    const url = URL.createObjectURL(file);
    console.log("fileType", file);
    setVideoUrl(url)
    if (file) {
      setIsVideoUploading(true); // start spinner
      if (fileType === 'video') {
        const isValid = await validateVideoClient(file);
        if (!isValid.status) {
          toast.warn(isValid.message, { position: 'bottom-right', theme: 'colored' });
          setIsVideoUploading(false); // start spinner
          return;
        };
      }

      try {
        const response = await uploadWithPresignedUrl(file, ImageType.CAMPAIGN_VIDEO).catch(err => {
          setIsVideoUploading(false); // stop spinner
          handleError(err)
        });
        // Optional: replace this with actual upload logic if needed
        const videoUrl = {
          eventId: event?.id,
          eventMedia: response,
          mediaType: "video"
        };

        // Simulate delay or call upload API if needed
        setTimeout(() => {
          setSelectedVideo(videoUrl);
          setIsVideoUploading(false); // stop spinner
        }, 1000);
        // setSelectedFile((prevImages) => [...(prevImages || []), videoUrl]);
      } catch (err) {
        console.error("Error uploading video", err);
        setIsVideoUploading(false); // stop spinner even on error
      }
    }
  };


  // Remove Image
  const removeImage = (index) => {
    const updatedImages = selectedFile.filter((_, i) => i !== index);
    setSelectedFile(updatedImages);
  };

  // Slider Settings
  const sliderSettings = {
    dots: true,
    infinite: selectedFile?.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: selectedFile?.length > 1,
    autoplaySpeed: 3000,
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [focusPoint, setFocusPoint] = useState({ x: 0.5, y: 0.5 });

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsModalOpen(true);
  };

  const handleSaveFocusPoint = (point) => {
    setFocusPoint(point);
    setIsModalOpen(false);
  };

  const [modalOpen, setModalOpen] = useState(false);
  const sliderRef = useRef(null);



  const handleDeleteVideo = () => {
    setVideoUrl(null);
  };


  return (
    <div className="border p-4 rounded">
      <h2 className="mb-3">Add Images & Video</h2>

      {/* Image Upload Section */}
      <div className="mb-4">
        <h4 className="mb-3">Images</h4>

        <div className="eventImageUpload w-md-50 border rounded p-4 text-center mb-2 mx-auto">
          {typeof selectedFile === 'object' && selectedFile.length > 0 ? (
            <>
              {/* Slider */}
              <Slider ref={sliderRef}
                {
                ...sliderSettings
                }
              >
                {selectedFile.map((url, index) => (
                  <div key={index} className="position-relative image-container">
                    <Card className="border-0" onClick={() => handleImageClick(url.eventMedia)}>
                      <div className="overlay-container">
                        <CardImg
                          top
                          src={url.eventMedia}
                          alt={`Uploaded ${index}`}
                          className="rounded image"
                          style={{
                            objectFit: 'cover',
                            objectPosition: `${focusPoint.x * 100}% ${focusPoint.y * 100}%`,
                            cursor: 'pointer',
                            height: "300px"
                          }}
                        />
                        <div className="overlay">
                          <Button
                            color="danger"
                            size="sm"
                            className="delete-button p-3 rounded-circle cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeImage(index);
                            }}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </div>
                ))}
              </Slider>
            </>
          ) : (
            <>
              <p className="text-muted d-flex flex-column align-items-center">
                <FontAwesomeIcon icon={faImage} />
                Drag and drop or
              </p>
              <Input type="file" accept="image/*" multiple onChange={handleImageUpload} className="d-none" id="imageUpload" />
              <label htmlFor="imageUpload" className="btn btn-outline-primary">
                Upload Images {isUploading && <Spinner size="sm" color="primary" />}
              </label>
            </>
          )}
        </div>
        {selectedFile.length > 0 && <div className="d-flex align-items-center justify-content-start my-3 flex-wrap gap-2">
          {/* Add more */}
          <label htmlFor="addMoreImages" className="d-flex align-items-center justify-content-center border border-primary rounded-3 m-0 mr-2 rounded" style={{ width: '100px', height: '60px', cursor: 'pointer' }}>
            <FontAwesomeIcon icon={faPlus} className="text-primary" />
          </label>
          <Input type="file" accept="image/*" multiple id="addMoreImages" onChange={handleImageUpload} className="d-none" disabled={selectedFile.length >= 5} />

          {/* Thumbs */}
          {selectedFile.map((item, i) => (
            <img
              key={i}
              src={item.eventMedia}
              alt={`Thumb ${i}`}
              style={{
                width: "100px",
                height: "60px",
                objectFit: "cover",
                borderRadius: "5px",
                border: "2px solid #ccc",
                cursor: "pointer"
              }}
              className="mr-2"
              onClick={() => sliderRef.current?.slickGoTo(i)}
            />
          ))}
        </div>}
        <ul className="event-image-info d-flex justify-content-around align-items-center list-style" style={{ fontSize: "0.8rem" }}>
          <li>
            • Pro tip: Use high-quality images to set the mood.
          </li>
          <li>
            • Supported file types: .jpg, .png, | Max size: {maxSizeMB}MB
          </li>
        </ul>
      </div>



      {/* Video Upload Section */}
      <div className="mt-4">
        <h4 className="mb-3">Video</h4>
        {/* If video is uploaded, show preview */}
        {videoUrl ? (
          <div className="position-relative d-inline-block">
            <video
              src={videoUrl}
              controls
              className="rounded"
              style={{ width: "240px", height: "auto" }}
            />
            <button
              className="btn btn-link position-absolute"
              onClick={handleDeleteVideo}
              style={{ top: "-8px", right: "-50px", color: "gray", fontSize: "1rem" }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        ) : (
          <div className="border rounded p-4 text-center">
            <Input
              type="file"
              accept="video/*"
              onChange={handleVideoUpload}
              className="d-none"
              id="videoUpload"
            />
            <label htmlFor="videoUpload" className="btn btn-outline-primary">
              Upload Video {isVideoUploading && <Spinner size="sm" color="primary" />}
            </label>
          </div>
        )}

        <ul
          className="event-image-info d-flex justify-content-around align-items-center list-style mt-2"
          style={{ fontSize: "0.8rem" }}
        >
          <li>• Add a video to enhance engagement. Max length: 1 min.</li>
          <li>• Supported file types: .mp4 | Max size: {maxSizeMB}MB</li>
          <li
            className="text-primary cursor-pointer"
            onClick={() => setModalOpen(true)}
          >
            View more details <FontAwesomeIcon icon={faArrowRightLong} />
          </li>
        </ul>
      </div>


      {/* Show API Data
      <pre className="mt-3 bg-light p-3 rounded">
        API Data: {JSON.stringify(apiData, null, 2)}
      </pre> */}
      <ImageFocusModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveFocusPoint}
        imageSrc={selectedImage}
      />
      <VideoRequirementsModal isOpen={modalOpen} toggle={() => setModalOpen(false)} />
    </div>
  );
};

export default ImageUpload;
